// Gift keys the user has bought, so they can copy one again or see whether it's been redeemed. Hidden when there are none.
import { motion } from "motion/react";
import { useApi } from "../../api.ts";
import { fmtRelative } from "../../format.ts";
import { Badge, cn, Icon, useToast } from "../../ui/index.tsx";
import { EASE } from "../member/kit.tsx";
import type { GiftPurchase } from "../../../shared/types.ts";

export function Gifts() {
  const gifts = useApi<GiftPurchase[]>("/me/gifts");
  const toast = useToast();
  const list = gifts.data ?? [];
  if (list.length === 0) return null;
  const copy = async (key: string) => { await navigator.clipboard.writeText(key); toast("Key copied", "success"); };
  return (
    <section id="gifts" className="pane hud p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="kicker mb-1">// gifts</div>
          <h2 className="text-lg font-semibold">Gifts you've bought</h2>
        </div>
        <Icon name="gift" className="size-5 text-accent" />
      </div>
      <ul className="space-y-2.5">
        {list.map((g, i) => (
          <motion.li key={g.session_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, ease: EASE }}
            className="rounded-lg border border-line bg-bg/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{g.name}</div>
                <div className="text-xs text-faint">{g.label} · bought {fmtRelative(g.created_at)}</div>
              </div>
              {g.revoked ? <Badge tone="bad">Revoked</Badge> : g.redeemed ? <Badge tone="ok" dot>Redeemed</Badge> : <Badge tone="info">Unused</Badge>}
            </div>
            {!g.revoked && !g.redeemed && (
              <button type="button" onClick={() => void copy(g.key)} title="Copy key"
                className={cn("mt-2.5 flex w-full items-center justify-between gap-2 rounded-md border border-line bg-surface/60 px-2.5 py-1.5 font-mono text-xs transition-colors hover:border-accent/50")}>
                <span className="truncate">{g.key}</span><Icon name="copy" className="size-3.5 shrink-0 text-faint" />
              </button>
            )}
          </motion.li>
        ))}
      </ul>
    </section>
  );
}
